import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Container from "./container";
import Marquee from "../marquee/marquee";

gsap.registerPlugin(ScrollTrigger);

ScrollTrigger.config({ ignoreMobileResize: true });

export default function Gallery() {
  const rootRef = useRef(null);
  const sectionRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!root || !section || !track) return;

    const ctx = gsap.context(() => {
      const items = gsap.utils.toArray(".gallery-item");
      const innerTargets = gsap.utils.toArray(".gallery-item > div");
      const progressBar = root.querySelector(".gallery-progress-bar");

      const getScrollDistance = () =>
        Math.max(track.scrollWidth - window.innerWidth, 0);

      const refreshScroll = () => ScrollTrigger.refresh();

      gsap.set(innerTargets, {
        scale: 0.94,
        y: 24,
        rotation: (index) => (index % 2 === 0 ? -2 : 2),
      });

      if (progressBar) {
        gsap.set(progressBar, { scaleX: 0, transformOrigin: "left center" });
      }

      const horizontalTween = gsap.to(track, {
        x: () => -getScrollDistance(),
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${getScrollDistance()}`,
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      if (progressBar) {
        gsap.to(progressBar, {
          scaleX: 1,
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: () => `+=${getScrollDistance()}`,
            scrub: true,
          },
        });
      }

      items.forEach((item, index) => {
        const target = item.firstElementChild;
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            containerAnimation: horizontalTween,
            start: "left 92%",
            end: "left 5%",
            scrub: 0.8,
          },
        });

        tl.to(target, {
          scale: 1,
          y: 0,
          rotation: 0,
          x: 0,
          ease: "power2.out",
          duration: 42,
        })
        .to(target, {
          scale: 0.96,
          y: -12,
          x: -20 * (index % 2 === 0 ? 1 : -1),
          ease: "power2.in",
          duration: 35,
        }, "+=10"); 
      });

      const images = section.querySelectorAll("img");
      let pending = images.length;

      const onImageReady = () => {
        pending -= 1;
        if (pending <= 0) refreshScroll();
      };

      if (pending === 0) {
        refreshScroll();
      } else {
        images.forEach((img) => {
          if (img.complete) onImageReady();
          else {
            img.addEventListener("load", onImageReady);
            img.addEventListener("error", onImageReady);
          }
        });
      }

      let lastWidth = window.innerWidth;
      const handleResize = () => {
        if (window.innerWidth !== lastWidth) {
          lastWidth = window.innerWidth;
          refreshScroll();
        }
      };
      window.addEventListener("resize", handleResize);

      return () => window.removeEventListener("resize", handleResize);
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div id="gallery" ref={rootRef} className="min-h-screen bg-transparent scroll-mt-24">
      <div>
        <Marquee text="Gallery" align="left" />
      </div>

      <div
        ref={sectionRef}
        className="relative overflow-hidden bg-transparent"
        style={{ height: "100vh" }}
      >
        {/* CHANGED: max-md:items-start shifts the gallery upward right below the marquee on mobile, while md:items-center preserves desktop composition */}
        <div
          ref={trackRef}
          className="relative z-10 flex flex-none items-center max-md:items-start max-md:pt-16 h-full will-change-transform"
          style={{ width: "max-content" }}
        >
          {/* CHANGED: Reduced entry spacing on mobile (max-md:w-6) so card aligns beautifully */}
          <div
            className="shrink-0 max-md:w-6"
            style={{ width: "clamp(60px, 10vw, 140px)" }}
          />

          <div className="flex flex-none items-center max-md:items-start h-full">
            <Container />
          </div>

          <div
            className="gallery-scroll-spacer shrink-0"
            style={{ width: "clamp(120px, 45vw, 720px)" }}
          />
        </div>

        <div className="absolute bottom-8 left-1/2 z-20 w-[min(320px,70vw)] -translate-x-1/2 pointer-events-none">
          <div className="h-[3px] rounded-full bg-[#cc5e15]/20 overflow-hidden">
            <div className="gallery-progress-bar h-full w-full rounded-full bg-[#cc5e15]" />
          </div>
        </div>
      </div>
    </div>
  );
}